import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  IconButton,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Inventory2 as InventoryIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../api';
import Autocomplete from '@mui/material/Autocomplete';

function AdminDashboard() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCompartment, setSelectedCompartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    image: '',
    compartments: [],
    equipment: []
  });

  // Normalize a name for sorting: drop everything before the first letter, lowercase, German locale
  const normalizeForSort = (name = '') => {
    try {
      const m = String(name).match(/\p{L}.*$/u);
      const core = m ? m[0] : String(name);
      return core.toLowerCase();
    } catch (e) {
      return String(name).toLowerCase();
    }
  };

  const sortByNameAlpha = useCallback(
    (a, b) => normalizeForSort(a.name).localeCompare(normalizeForSort(b.name), 'de', { sensitivity: 'base' }),
    []
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [vehiclesRes, equipmentRes] = await Promise.all([
        api.get('/vehicles'),
        api.get('/equipment')
      ]);

      const vehiclesData = vehiclesRes.data;
      const equipmentData = equipmentRes.data;

      setVehicles(vehiclesData);
      // keep a stable, alphabetically sorted equipment list (ignore leading numbers/symbols)
      setEquipment([...equipmentData].sort(sortByNameAlpha));

      setError(null);
      return { vehiclesData, equipmentData };
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [sortByNameAlpha]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setSelectedCompartment(null);
  };

  const handleCompartmentSelect = (compartment) => {
    setSelectedCompartment(compartment);
  };

  const handleDialogOpen = (type, data = null) => {
    setDialogType(type);
    if (data) {
      if (type === 'vehicle') {
        setFormData({
          id: data.id,
          name: data.name,
          description: data.description || '',
          image: data.image || '',
          compartments: data.compartments || [],
          equipment: []
        });
      } else if (type === 'compartment') {
        setFormData({
          id: data.id,
          name: data.name,
          image: data.image || '',
          equipment: data.equipment || []
        });
      } else if (type === 'equipment') {
        setFormData({
          id: data.id,
          name: data.name
        });
      }
    } else {
      if (type === 'vehicle') {
        setFormData({
          id: null,
          name: '',
          description: '',
          image: '',
          compartments: [],
          equipment: []
        });
      } else if (type === 'compartment') {
        setFormData({
          id: null,
          name: '',
          image: '',
          equipment: []
        });
      } else if (type === 'equipment') {
        setFormData({
          id: null,
          name: ''
        });
      }
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormData({
      id: null,
      name: '',
      description: '',
      image: '',
      compartments: [],
      equipment: []
    });
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'vehicle') {
        if (formData.id) {
          // Update existing vehicle
          await api.put(`/vehicles/${formData.id}`, formData);
        } else {
          // Create new vehicle
          await api.post(`/vehicles`, formData);
        }
      } else if (dialogType === 'compartment') {
        if (formData.id) {
          // Update existing compartment
          await api.put(`/vehicles/${selectedVehicle.id}/compartments/${formData.id}`, formData);
        } else {
          // Create new compartment
          await api.post(`/vehicles/${selectedVehicle.id}/compartments`, formData);
        }
      } else if (dialogType === 'equipment') {
        if (formData.id) {
          // Update existing equipment
          await api.put(`/equipment/${formData.id}`, formData);
        } else {
          // Create new equipment
          await api.post(`/equipment`, formData);
        }
      }
      handleDialogClose();
      const result = await fetchData();
      if (result && selectedVehicle) {
        const updatedVehicle = result.vehiclesData.find((v) => v.id === selectedVehicle.id);
        setSelectedVehicle(updatedVehicle || null);
        if (updatedVehicle && selectedCompartment) {
          const updatedCompartment = updatedVehicle.compartments.find((c) => c.id === selectedCompartment.id);
          setSelectedCompartment(updatedCompartment || null);
        }
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      const confirmed = window.confirm('Bist du sicher, dass du das löschen möchtest?');
      if (!confirmed) return;
      if (type === 'vehicle') {
        await api.delete(`/vehicles/${id}`);
      } else if (type === 'compartment') {
        await api.delete(`/vehicles/${selectedVehicle.id}/compartments/${id}`);
      } else if (type === 'equipment') {
        await api.delete(`/equipment/${id}`);
      }
      const result = await fetchData();
      if (result) {
        if (type === 'vehicle') {
          setSelectedVehicle(null);
          setSelectedCompartment(null);
        } else if (type === 'compartment') {
          const updatedVehicle = result.vehiclesData.find((v) => v.id === selectedVehicle?.id);
          setSelectedVehicle(updatedVehicle || null);
          setSelectedCompartment(null);
        }
      }
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Lade Daten...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Zurück zur Auswahl
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Verwaltung
        </Typography>

        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Fahrzeuge" />
            <Tab label="Ausrüstung" />
          </Tabs>
        </Paper>

        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Fahrzeuge
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleDialogOpen('vehicle')}
                  >
                    Neues Fahrzeug
                  </Button>
                </Box>
                <List>
                  {vehicles.map((vehicle) => (
                  <ListItem key={vehicle.id} disablePadding>
                    <ListItemButton
                      selected={selectedVehicle?.id === vehicle.id}
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      <ListItemText 
                        primary={vehicle.name}
                        secondary={vehicle.description}
                        secondaryTypographyProps={{ sx: { whiteSpace: 'pre-line' } }}
                      />
                    </ListItemButton>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVehicleSelect(vehicle);
                        }}
                        title="Gerätefächer anzeigen"
                      >
                        <InventoryIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDialogOpen('vehicle', vehicle);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete('vehicle', vehicle.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {selectedVehicle && (
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      Gerätefächer: {selectedVehicle.name}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleDialogOpen('compartment')}
                    >
                      Neues Gerätefach
                    </Button>
                  </Box>
                  <List>
                    {selectedVehicle.compartments.map((compartment) => (
                      <React.Fragment key={compartment.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={selectedCompartment?.id === compartment.id}
                            onClick={() => handleCompartmentSelect(compartment)}
                          >
                            <ListItemText
                              primary={compartment.name}
                              secondary={`${compartment.equipment.length} Ausrüstungsgegenstände`}
                            />
                          </ListItemButton>
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDialogOpen('compartment', compartment);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete('compartment', compartment.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {activeTab === 1 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Ausrüstung</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleDialogOpen('equipment')}
              >
                Neue Ausrüstung
              </Button>
            </Box>
            <List>
              {equipment.map((item) => (
                <ListItem key={item.id}>
                  <ListItemText primary={item.name} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDialogOpen('equipment', item)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDelete('equipment', item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogType === 'vehicle' 
              ? (formData.id ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug')
              : dialogType === 'compartment'
              ? (formData.id ? 'Gerätefach bearbeiten' : 'Neues Gerätefach')
              : (formData.id ? 'Ausrüstung bearbeiten' : 'Neue Ausrüstung')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              {dialogType === 'vehicle' && (
                <>
                  <TextField
                    fullWidth
                    label="Beschreibung"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={2}
                    minRows={2}
                    sx={{ mb: 2, whiteSpace: 'pre-line' }}
                  />
                  <TextField
                    fullWidth
                    label="Bild-URL"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ mb: 2 }}
                    disabled={uploading}
                  >
                    {uploading ? 'Lade hoch...' : 'Bild hochladen'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          setUploading(true);
                          const fd = new FormData();
                          fd.append('file', file);
                          const res = await api.post('/uploads', fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          const uploadedUrl = res.data.absoluteUrl || res.data.url;
                          setFormData((prev) => ({ ...prev, image: uploadedUrl }));
                        } catch (err) {
                          setError(err.message || 'Upload fehlgeschlagen');
                        } finally {
                          setUploading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </Button>
                </>
              )}
              {dialogType === 'compartment' && (
                <>
                  <TextField
                    fullWidth
                    label="Bild-URL"
                    value={formData.image || ''}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ mb: 2 }}
                    disabled={uploading}
                  >
                    {uploading ? 'Lade hoch...' : 'Bild hochladen'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          setUploading(true);
                          const fd = new FormData();
                          fd.append('file', file);
                          const res = await api.post('/uploads', fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          const uploadedUrl = res.data.absoluteUrl || res.data.url;
                          setFormData((prev) => ({ ...prev, image: uploadedUrl }));
                        } catch (err) {
                          setError(err.message || 'Upload fehlgeschlagen');
                        } finally {
                          setUploading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </Button>
                  <Autocomplete
                    multiple
                    options={[...equipment].sort(sortByNameAlpha)}
                    getOptionLabel={(option) => option.name}
                    value={(equipment || []).filter((e) => (formData.equipment || []).includes(e.id))}
                    onChange={(_e, newValue) =>
                      setFormData({ ...formData, equipment: newValue.map((v) => v.id) })
                    }
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => (
                        <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ausrüstung"
                        placeholder="Suchen und auswählen"
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Abbrechen</Button>
            <Button onClick={handleSave} variant="contained" color="primary">
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default AdminDashboard; 
